import { Pressable, Text, View } from "react-native";

interface TutorReviewSummaryProps {
  averageRating: number;
  reviewCount: number;
}

const STAR_SIZE = 18;

function getStarType(
  index: number,
  rating: number,
): "full" | "half" | "empty" {
  if (rating >= index) return "full";
  if (rating >= index - 0.5) return "half";
  return "empty";
}

function SummaryStar({ type }: { type: "full" | "half" | "empty" }) {
  const starClass = "text-base leading-none text-amber-500";

  if (type === "full") {
    return (
      <View style={{ width: STAR_SIZE }} className="items-center justify-center">
        <Text className={starClass}>★</Text>
      </View>
    );
  }

  if (type === "empty") {
    return (
      <View style={{ width: STAR_SIZE }} className="items-center justify-center">
        <Text className={starClass}>☆</Text>
      </View>
    );
  }

  return (
    <View
      style={{ width: STAR_SIZE, height: STAR_SIZE }}
      className="relative items-center justify-center"
    >
      <Text className={`${starClass} absolute`}>☆</Text>
      <View
        className="absolute left-0 top-0 overflow-hidden"
        style={{ width: STAR_SIZE / 2, height: STAR_SIZE }}
      >
        <Text className={starClass}>★</Text>
      </View>
    </View>
  );
}

function StarRatingSummary({ rating }: { rating: number }) {
  return (
    <View className="flex-row items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <SummaryStar key={star} type={getStarType(star, rating)} />
      ))}
    </View>
  );
}

function TutorReviewSummary({
  averageRating,
  reviewCount,
  onReviewCountPress,
}: TutorReviewSummaryProps & {
  onReviewCountPress?: () => void;
}) {
  return (
    <View className="flex-row items-center gap-2">
      <Text className="font-geologica-medium text-base text-black">
        {averageRating.toFixed(1)}
      </Text>
      <StarRatingSummary rating={averageRating} />
      <Pressable
        onPress={onReviewCountPress}
        disabled={!onReviewCountPress}
        className="active:opacity-70"
      >
        <Text className="font-geologica-regular text-base text-teal-600">
          ({reviewCount})
        </Text>
      </Pressable>
    </View>
  );
}

export function TutorReviewSummaryOrEmpty({
  averageRating,
  reviewCount,
  onReviewCountPress,
}: {
  averageRating: number | null;
  reviewCount: number;
  onReviewCountPress?: () => void;
}) {
  if (reviewCount === 0 || averageRating === null) {
    return null;
  }

  return (
    <TutorReviewSummary
      averageRating={averageRating}
      reviewCount={reviewCount}
      onReviewCountPress={onReviewCountPress}
    />
  );
}

export default TutorReviewSummary;
